import type { PostHogConfig } from "posthog-js";

import {
  isTokenRoute,
  redactTokenProperties,
} from "@/lib/analytics/redact-token-urls";
import { isChunkLoadError } from "@/lib/pwa/chunk-load-error";
import { PRODUCT, PRODUCT_DEF } from "@/lib/product";
import { analyticsEnvironment } from "./traffic-context";

const CHUNK_CASCADE_WINDOW_MS = 10_000;
const FETCH_NETWORK_ERROR =
  /^(?:Failed to fetch|Load failed|fetch failed|NetworkError when attempting to fetch resource\.?)$/i;
// Browser noise with no defect behind it. The ResizeObserver "loop" error is
// the browser deferring a resize notification to the next frame (7 in the 20
// days to 2026-10-10, all autocaptured from window.onerror). An AbortError is a
// request something cancelled on purpose: a navigation, an unmount, a newer
// query. A TimeoutError is not matched, so a timed-out request stays visible.
const RESIZE_OBSERVER_LOOP =
  /^ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/;

function isBenignBrowserException(type: string, value: string): boolean {
  if (RESIZE_OBSERVER_LOOP.test(value)) return true;
  return type === "AbortError" || /^AbortError(?::|$)/.test(value);
}

/**
 * PostHog browser SDK options. Ingestion goes through the same-origin
 * `/ingest` reverse proxy (see next.config.mjs rewrites) so ad-blockers and
 * the document CSP never interfere — `connect-src 'self'` already covers it.
 *
 * `defaults` pins the modern best-practice bundle (history-change pageviews,
 * pageleave, autocapture, web vitals). Options set explicitly below override
 * the bundle so the privacy-sensitive ones are pinned regardless of version.
 *
 * @param pathname The page's initial `window.location.pathname`, used to
 * gate session recording off signing/share token routes. Defaults to `""`
 * (not a token route) so existing callers/tests that don't pass one keep
 * recording enabled.
 */
export function buildPostHogConfig(
  pathname: string = "",
): Partial<PostHogConfig> {
  let recentChunkLoadAt: number | null = null;
  const tokenRoute = isTokenRoute(pathname);

  return {
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    // Signing/share-link tokens live in the URL of these routes. before_send
    // also runs on `$snapshot` (replay) events, and rrweb's Meta event embeds
    // the full `href` inside `$snapshot_data` — redacting URL properties
    // isn't enough to keep a bearer token out of a replay, so recording is
    // disabled outright for the whole page load. Token routes are always
    // entered fresh from an email/SMS link, so the initial pathname alone is
    // enough to gate on.
    disable_session_recording: tokenRoute,
    // No person profile for anonymous marketing visitors — created on identify().
    person_profiles: "identified_only",
    // PostHog error tracking: autocapture uncaught errors + unhandled rejections.
    capture_exceptions: true,
    // Interaction-friction signals are supported directly by posthog-js.
    // Keep element contents private: target type/classes remain useful for
    // debugging dead/rage clicks without recording visible text or attrs.
    // Token routes capture no clicks at all: autocapture copies a clicked
    // anchor's href — there, the signed copy's presigned URL — into
    // attr__href / $elements_chain / $external_click_url regardless of
    // mask_all_element_attributes, and heatmaps buffer by the full
    // location.href, which is the signing token. A local `false` beats the
    // project's remote setting for each of these.
    rageclick: !tokenRoute,
    capture_dead_clicks: !tokenRoute,
    // Nothing persisted on a token route: posthog-js stores
    // $initial_person_info.u — the page's full href, the signing token
    // included — in its first-party cookie (365 days) and localStorage at
    // init, before any page code runs and out of before_send's reach.
    // "memory" keeps that state for this page load only.
    ...(tokenRoute
      ? {
          autocapture: false,
          capture_heatmaps: false,
          persistence: "memory" as const,
        }
      : {}),
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Enforce exception ownership at the last client ingestion boundary:
    // - Drop client-side "server-shadow" exceptions after a server 5xx; the
    //   server already owns the useful stack and cause chain.
    // - Keep a stale chunk-load root, but drop the immediate generic fetch
    //   failures that the same broken page fans out through React Query.
    //
    // Both rules are scoped to TanStack mutation/query events via the `scope`
    // property. Standalone network errors and similarly named domain errors
    // remain visible.
    // - Drop benign browser noise from any source (isBenignBrowserException).
    before_send: (event) => {
      if (!event) return event;
      // Preserve production token redaction before adding funnel metadata.
      redactTokenProperties(event);
      // Also covers the first pageview and events immediately after reset().
      event.properties.product ??= PRODUCT;
      event.properties.signup_product ??= PRODUCT_DEF.signupProduct;
      event.properties.analytics_version = 2;
      event.properties.analytics_environment = analyticsEnvironment(
        typeof window === "undefined" ? "" : window.location.hostname,
      );
      if (event.event !== "$exception") return event;
      const rawScope: unknown = event.properties.scope;
      const scope = typeof rawScope === "string" ? rawScope : "";
      const list = (event.properties.$exception_list ?? []) as Array<{
        type?: string;
        value?: string;
      }>;
      const type = list[0]?.type ?? "";
      const value = list[0]?.value ?? "";

      if (isBenignBrowserException(type, value)) return null;

      if (isChunkLoadError({ name: type, message: value })) {
        recentChunkLoadAt = Date.now();
        return {
          ...event,
          properties: {
            ...event.properties,
            exception_owner: "chunk-load",
            navigator_online:
              typeof navigator === "undefined" ? "unknown" : navigator.onLine,
            service_worker_controlled:
              typeof navigator !== "undefined" &&
              "serviceWorker" in navigator &&
              navigator.serviceWorker.controller !== null,
          },
        };
      }

      if (!scope.startsWith("tanstack-")) return event;
      if (
        recentChunkLoadAt !== null &&
        Date.now() - recentChunkLoadAt <= CHUNK_CASCADE_WINDOW_MS &&
        FETCH_NETWORK_ERROR.test(value)
      ) {
        return null;
      }
      if (/_failed_5\d{2}$|^Internal Server Error$/.test(value)) return null;
      return event;
    },
    // Financial + PII-heavy product — mask everything in replay by default.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      // A local `false` always beats the project's remote PostHog dashboard
      // setting for both of these — posthog-js only falls back to the
      // remote value when the local one is strictly `undefined`, not
      // `false` (posthog-js/lib/src/extensions/replay/external/config.js,
      // buildNetworkRequestOptions).
      recordHeaders: false,
      recordBody: false,
      // Same local-wins-over-remote precedence, via `??` against the remote
      // value (lazy-loaded-session-recorder.js, _canvasRecording getter) —
      // `false` is not nullish, so the local value always wins.
      captureCanvas: { recordCanvas: false },
    },
    // Web vitals are dispatched explicitly by `WebVitalsTracker` using
    // Next's `useReportWebVitals`. Disable PostHog's autocapture path so
    // both don't fire and double the event volume.
    capture_performance: { web_vitals: false },
  };
}
